import { useState } from "react";
import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  EmptyState,
  FormTextarea,
  QueryStateBoundary,
  TabContent,
  TabList,
  TabTrigger,
  Tabs,
} from "@/components/ui";
import { centsToUsd } from "@/lib/money";
import {
  useReconciliationFlags,
  useResolveFlag,
  useRunReconcile,
} from "@/api/hooks/useReconciliation";
import type { ReconciliationFlag, ReconciliationStatus } from "@/api/types/reconciliation";

const STATUS_TABS: { value: ReconciliationStatus; label: string }[] = [
  { value: "open", label: "Open" },
  { value: "resolved", label: "Resolved" },
  { value: "ignored", label: "Ignored" },
];

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function KindGlyph() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-7 w-7 text-copper"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 3v18M17 6.5c0-1.93-2.24-3.5-5-3.5S7 4.57 7 6.5 9.24 10 12 10s5 1.57 5 3.5-2.24 3.5-5 3.5-5-1.57-5-3.5" />
    </svg>
  );
}

/**
 * V2 feature #2's "money to review" queue — GET /v2/reconciliation. Every
 * row is a Stripe amount this system's own Payment ledger couldn't safely
 * book on its own (a captured-but-unbooked payment from the webhook's
 * overpay/void guard, or a mismatch surfaced by the nightly reconcile
 * sweep, app/services/reconciliation.py::reconcile_stripe) — resolving one
 * here does NOT touch the ledger; it's a human saying "I looked at this,
 * it's handled" (or "ignore it").
 */
export function ReconciliationPage() {
  const [status, setStatus] = useState<ReconciliationStatus>("open");
  const [resolvingFlag, setResolvingFlag] = useState<ReconciliationFlag | null>(null);

  const { data, isLoading, error, refetch } = useReconciliationFlags(status);
  const resolveFlag = useResolveFlag();
  const runReconcile = useRunReconcile();

  const flags = data?.items ?? [];

  return (
    <div className="p-6 sm:p-8">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-text-primary">Reconciliation</h1>
          <p className="mt-1 text-sm text-text-secondary">
            Stripe amounts the ledger couldn't safely book on its own — review, then resolve
            or ignore.
          </p>
        </div>
        <Button
          variant="secondary"
          onClick={() => runReconcile.mutate()}
          disabled={runReconcile.isPending}
          data-testid="run-reconcile-button"
        >
          {runReconcile.isPending ? "Running…" : "Run reconcile now"}
        </Button>
      </div>

      {runReconcile.data && (
        <p className="mb-4 text-xs text-text-muted" data-testid="reconcile-run-summary">
          {runReconcile.data.enabled
            ? `Scanned ${runReconcile.data.scanned}, flagged ${runReconcile.data.flagged} new item${runReconcile.data.flagged === 1 ? "" : "s"}.`
            : "Stripe isn't configured — nothing to reconcile."}
        </p>
      )}

      <Tabs value={status} onValueChange={(v) => setStatus(v as ReconciliationStatus)}>
        <TabList>
          {STATUS_TABS.map((tab) => (
            <TabTrigger key={tab.value} value={tab.value}>
              {tab.label}
            </TabTrigger>
          ))}
        </TabList>

        {STATUS_TABS.map((tab) => (
          <TabContent key={tab.value} value={tab.value}>
            <QueryStateBoundary isLoading={isLoading} error={error} onRetry={() => void refetch()}>
              {flags.length === 0 ? (
                <EmptyState
                  icon={<KindGlyph />}
                  title={tab.value === "open" ? "Nothing to review" : `No ${tab.value} items`}
                  description={
                    tab.value === "open"
                      ? "Every Stripe amount is accounted for in the ledger."
                      : undefined
                  }
                />
              ) : (
                <FlagsTable
                  flags={flags}
                  canResolve={tab.value === "open"}
                  onResolve={setResolvingFlag}
                />
              )}
            </QueryStateBoundary>
          </TabContent>
        ))}
      </Tabs>

      <ResolveFlagDialog
        flag={resolvingFlag}
        isSubmitting={resolveFlag.isPending}
        onCancel={() => setResolvingFlag(null)}
        onConfirm={(note, ignore) => {
          if (!resolvingFlag) return;
          resolveFlag.mutate(
            { id: resolvingFlag.id, note: note || undefined, ignore },
            { onSuccess: () => setResolvingFlag(null) },
          );
        }}
      />
    </div>
  );
}

function FlagsTable({
  flags,
  canResolve,
  onResolve,
}: {
  flags: ReconciliationFlag[];
  canResolve: boolean;
  onResolve: (flag: ReconciliationFlag) => void;
}) {
  return (
    <div
      className="overflow-x-auto rounded-lg border border-border"
      data-testid="reconciliation-table"
    >
      <table className="w-full text-left text-sm">
        <thead className="border-b border-border bg-bg-hover/50">
          <tr>
            <th className="px-4 py-2">Kind</th>
            <th className="px-4 py-2">Amount</th>
            <th className="px-4 py-2">Stripe object</th>
            <th className="px-4 py-2">Detail</th>
            <th className="px-4 py-2">Created</th>
            {canResolve && <th className="px-4 py-2" />}
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {flags.map((flag) => (
            <tr key={flag.id} data-testid="reconciliation-row">
              <td className="px-4 py-3">
                <Badge variant="secondary" size="sm">
                  {flag.kind.replaceAll("_", " ")}
                </Badge>
              </td>
              <td className="tabular px-4 py-3 font-semibold text-text-primary">
                {flag.amount_cents !== null ? centsToUsd(flag.amount_cents) : "—"}
              </td>
              <td className="px-4 py-3 font-mono text-xs text-text-secondary">
                {flag.stripe_object_id ?? "—"}
              </td>
              <td className="max-w-xs px-4 py-3 text-text-secondary">{flag.detail ?? "—"}</td>
              <td className="tabular px-4 py-3 text-xs text-text-muted">
                {formatDateTime(flag.created_at)}
              </td>
              {canResolve && (
                <td className="px-4 py-3 text-right">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => onResolve(flag)}
                    data-testid="resolve-flag-button"
                  >
                    Resolve
                  </Button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ResolveFlagDialog({
  flag,
  isSubmitting,
  onCancel,
  onConfirm,
}: {
  flag: ReconciliationFlag | null;
  isSubmitting: boolean;
  onCancel: () => void;
  onConfirm: (note: string, ignore: boolean) => void;
}) {
  const [note, setNote] = useState("");
  const [ignore, setIgnore] = useState(false);

  return (
    <Dialog
      open={flag !== null}
      onClose={onCancel}
      ariaLabel="Resolve reconciliation flag"
    >
      <DialogContent size="sm">
        <DialogHeader onClose={onCancel}>Resolve flag</DialogHeader>
        <DialogBody>
          <p className="mb-3 text-sm text-text-secondary">
            {flag?.detail ?? "This item will be marked handled."}
          </p>
          <FormTextarea
            label="Resolution note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="What happened / what you did about it (optional)"
          />
          <div className="mt-3">
            <Checkbox
              label="Ignore instead of resolve (not a real reconciliation issue)"
              checked={ignore}
              onChange={(e) => setIgnore(e.target.checked)}
            />
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={isSubmitting}
            onClick={() => {
              onConfirm(note.trim(), ignore);
              setNote("");
              setIgnore(false);
            }}
            data-testid="confirm-resolve-button"
          >
            {isSubmitting ? "Saving…" : ignore ? "Ignore" : "Mark resolved"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
